import { useState, useEffect } from 'react';
import { subscribeToDocument, getDocument } from '../firebase/firestore';

/**
 * Sanitize raffle data to remove sensitive fields.
 * Defense in depth - the real protection is Firestore rules,
 * but we filter client-side as extra protection.
 *
 * @param raffle - The raw raffle data
 * @param userId - Current user's ID (if authenticated)
 * @returns Sanitized raffle data, with pickupAddress included only for winner
 */
function sanitizeRaffle(raffle, userId = null) {
  if (!raffle) return null;

  // Destructure to exclude sensitive fields by default
  const {
    pickupAddress,
    ...safeData
  } = raffle;

  // Only reveal pickupAddress to the winner after draw
  const isWinner = raffle.status === 'completed' && userId && raffle.winnerId === userId;

  if (isWinner && pickupAddress) {
    return { ...safeData, pickupAddress };
  }

  return safeData;
}

/**
 * useRaffle - Fetches raffle data with populated seller info
 *
 * Subscribes to raffle document and fetches seller user data.
 * Security:
 * - Sanitizes pickupAddress (only winner sees after draw)
 * - Only exposes public seller info (displayName, photoURL, rating)
 *
 * @param raffleId - The raffle document ID
 * @param userId - Current user's ID (optional, used to reveal winner-only data)
 */
export function useRaffle(raffleId, userId = null) {
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!raffleId) {
      setLoading(false);
      return;
    }

    setError(null);

    const unsubscribe = subscribeToDocument(
      'raffles',
      raffleId,
      async (raffleData) => {
        if (!raffleData) {
          setRaffle(null);
          setLoading(false);
          return;
        }

        // Sanitize sensitive fields (passes userId to check if winner)
        const sanitizedRaffle = sanitizeRaffle(raffleData, userId);

        // Fetch seller data if sellerId exists
        if (sanitizedRaffle.sellerId) {
          try {
            const sellerData = await getDocument('users', sanitizedRaffle.sellerId);
            if (sellerData) {
              // Only expose public seller info
              setRaffle({
                ...sanitizedRaffle,
                seller: {
                  id: sanitizedRaffle.sellerId,
                  displayName: sellerData.displayName,
                  photoURL: sellerData.photoURL,
                  rating: sellerData.rating,
                  reviewCount: sellerData.reviewCount,
                  phoneVerified: sellerData.phoneVerified,
                  createdAt: sellerData.createdAt,
                },
              });
            } else {
              setRaffle(sanitizedRaffle);
            }
          } catch (err) {
            console.error('Failed to fetch seller data:', err);
            setRaffle(sanitizedRaffle);
          }
        } else {
          setRaffle(sanitizedRaffle);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Raffle subscription error:', err);
        setError('Unable to load competition');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [raffleId, userId]);

  return { raffle, loading, error };
}

export async function fetchRaffle(raffleId) {
  const raffle = await getDocument('raffles', raffleId);
  return sanitizeRaffle(raffle);
}
